import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Loader2 } from "lucide-react";
import emailjs from "@emailjs/browser";

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

export default function ContactUs() {
  const formRef = useRef(null);
  const [status, setStatus] = useState("idle"); // 'idle' | 'sending' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState("");
  const [formData, setFormData] = useState({ name: "", phone: "", bill: "", message: "" });

  async function onSubmit(e) {
    e.preventDefault();

    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      setErrorMsg("Email service not configured. Please contact us directly via phone or email.");
      setStatus("error");
      return;
    }

    setStatus("sending");
    setErrorMsg("");

    try {
      await emailjs.sendForm(
        SERVICE_ID,
        TEMPLATE_ID,
        formRef.current,
        { publicKey: PUBLIC_KEY }
      );
      setStatus("success");
      setTimeout(() => {
        setStatus("idle");
        setFormData({ name: "", phone: "", bill: "", message: "" });
      }, 4000);
    } catch (err) {
      console.error("EmailJS error:", err);
      setErrorMsg(
        err?.text ||
          "Something went wrong sending your inquiry. Please try again or contact us directly."
      );
      setStatus("error");
    }
  }

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const isSending = status === "sending";

  return (
    <section
      id="contacts"
      className="relative w-full bg-[#1A1A1A] text-white -mt-px"
    >
      <div className="max-w-[92rem] mx-auto px-5 sm:px-8 md:px-10 py-20 md:py-28 font-sora">
        <motion.h2
          className="text-3xl md:text-4xl font-bold font-sharetech text-center mb-2"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
        >
          Ready to Go Solar?
        </motion.h2>
        <motion.div
          className="h-1 w-32 bg-solar-gold/40 rounded-full mx-auto mb-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        />

        <motion.p
          className="mt-4 text-center text-white/80 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.15 }}
          viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
        >
          We reply within 24 hours. Tell us about your energy needs and we'll provide a personalized quote.
        </motion.p>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {/* Contact Info */}
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="space-y-8">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-accent-sun/20 text-accent-sun">
                    <Phone size={20} />
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-white mb-1">Phone</h3>
                  <a href="tel:+63-967-886-7909" className="text-white/80 hover:text-solar-gold transition">
                    +63 967 886 7909
                  </a>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-accent-sun/20 text-accent-sun">
                    <Mail size={20} />
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-white mb-1">Email</h3>
                  <a href="mailto:solares.energysolutions@gmail.com" className="text-white/80 hover:text-solar-gold transition text-sm">
                    solares.energysolutions@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-accent-sun/20 text-accent-sun">
                    <MapPin size={20} />
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-white mb-1">Location</h3>
                  <p className="text-white/80 text-sm">
                    Based in the Philippines<br />
                    Serving all major islands
                  </p>
                </div>
              </div>
            </div>

            {/* Response Time Badge */}
            <div className="p-4 rounded-lg bg-accent-green/10 border border-accent-green/30">
              <p className="text-sm text-white/80">
                <span className="font-bold text-accent-green">24-hour response</span> guaranteed
              </p>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            className="md:col-span-2"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            {status === "success" ? (
              <motion.div
                className="p-8 rounded-xl bg-accent-green/20 border-2 border-accent-green/50 text-center"
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring" }}
              >
                <div className="text-4xl mb-3">✓</div>
                <h3 className="text-2xl font-bold text-accent-green mb-2">
                  Thank you, {formData.name}!
                </h3>
                <p className="text-white/80">
                  Your inquiry has been sent. We'll call you within 24 hours to discuss your solar project.
                </p>
              </motion.div>
            ) : (
              <form
                ref={formRef}
                onSubmit={onSubmit}
                className="space-y-4"
                aria-label="Contact form"
              >
                <input
                  type="hidden"
                  name="to_email"
                  value="solares.energysolutions@gmail.com"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    id="name"
                    type="text"
                    name="from_name"
                    value={formData.name}
                    onChange={handleChange("name")}
                    required
                    disabled={isSending}
                    placeholder="Full Name"
                    className="w-full rounded-lg border border-solar-border bg-white/5 text-white placeholder-white/50 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-solar-gold transition disabled:opacity-60"
                  />
                  <input
                    id="phone"
                    type="tel"
                    name="from_phone"
                    value={formData.phone}
                    onChange={handleChange("phone")}
                    required
                    disabled={isSending}
                    placeholder="Phone Number"
                    className="w-full rounded-lg border border-solar-border bg-white/5 text-white placeholder-white/50 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-solar-gold transition disabled:opacity-60"
                  />
                </div>

                <input
                  id="bill"
                  type="text"
                  name="from_bill"
                  value={formData.bill}
                  onChange={handleChange("bill")}
                  disabled={isSending}
                  placeholder="Monthly Bill (₱) — Optional, helps us recommend systems"
                  className="w-full rounded-lg border border-solar-border bg-white/5 text-white placeholder-white/50 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-solar-gold transition disabled:opacity-60"
                />

                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange("message")}
                  required
                  disabled={isSending}
                  placeholder="Tell us about your project (rooftop type, current energy usage, etc.)"
                  className="w-full rounded-lg border border-solar-border bg-white/5 text-white placeholder-white/50 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-solar-gold transition resize-none disabled:opacity-60"
                />

                {status === "error" && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-300"
                  >
                    {errorMsg}
                  </motion.div>
                )}

                <button
                  type="submit"
                  disabled={isSending}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-accent-sun hover:bg-orange-600 disabled:bg-accent-sun/60 disabled:cursor-not-allowed text-white font-bold px-6 py-3.5 transition transform hover:translate-y-[-2px] hover:shadow-md disabled:transform-none disabled:hover:translate-y-0"
                >
                  {isSending ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <span>Send Inquiry</span>
                      <span>→</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
